import React, { useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Activity, Heart, Wind, Brain, Hand, Footprints,
  AlertTriangle, CheckCircle2, Clock, ArrowRight,
  Thermometer, Droplets, Eye, Ear, MessageSquare,
  Save, Send, ChevronRight, ChevronLeft, User,
  Mic, MicOff, Loader2, Volume2
} from "lucide-react";

// Body regions for the body map
const BODY_REGIONS = [
  { id: "head", label: "Head/Neurological", icon: Brain, x: 50, y: 8 },
  { id: "chest", label: "Chest/Respiratory", icon: Wind, x: 50, y: 28 },
  { id: "heart", label: "Cardiovascular", icon: Heart, x: 35, y: 28 },
  { id: "abdomen", label: "Abdomen/GI", icon: Activity, x: 50, y: 45 },
  { id: "skin_torso", label: "Skin (Torso)", icon: Droplets, x: 65, y: 38 },
  { id: "arms", label: "Upper Extremities", icon: Hand, x: 20, y: 40 },
  { id: "legs", label: "Lower Extremities", icon: Footprints, x: 50, y: 75 },
];

// HOPE symptom impact scales (0-3)
const SYMPTOM_SCALES = [
  { id: "pain", label: "Pain", color: "#ef4444", description: "Rate current pain level" },
  { id: "dyspnea", label: "Shortness of Breath", color: "#3b82f6", description: "Rate breathing difficulty" },
  { id: "nausea", label: "Nausea/Vomiting", color: "#22c55e", description: "Rate nausea severity" },
  { id: "anxiety", label: "Anxiety/Agitation", color: "#f59e0b", description: "Rate anxiety level" },
  { id: "fatigue", label: "Fatigue", color: "#8b5cf6", description: "Rate energy/fatigue level" },
];

// Functional status ADL items
const ADL_ITEMS = [
  { id: "eating", label: "Eating" },
  { id: "oral_hygiene", label: "Oral Hygiene" },
  { id: "toileting", label: "Toileting" },
  { id: "transfers", label: "Transfers" },
  { id: "bathing", label: "Bathing" },
  { id: "dressing", label: "Dressing Upper" },
];

// Care goals
const CARE_GOALS = [
  { id: "comfort", label: "Comfort Focused", description: "Focus on symptom management and quality of life" },
  { id: "longevity", label: "Longevity/Treatment", description: "Continue treatments to extend life" },
  { id: "spiritual", label: "Spiritual/Religious", description: "Focus on spiritual care and rituals" },
];

const HOPENavigator = ({ patientId, chartId, onComplete, onSave }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [assessmentData, setAssessmentData] = useState({
    // Physical Snapshot
    skin: {
      status: "intact", // intact, pressure, non_pressure
      stage: null,
      locations: []
    },
    respiratory: {
      status: "no_sob", // no_sob, sob_exertion, sob_rest
      oxygen: false,
      oxygenFlow: null
    },
    // Symptom Impact (0-3 scale)
    symptoms: {
      pain: 0,
      dyspnea: 0,
      nausea: 0,
      anxiety: 0,
      fatigue: 0
    },
    symptomFlags: {
      pain_new: false,
      dyspnea_new: false
    },
    // Functional Status
    functional: {
      changedADLs: [],
      overall_decline: false
    },
    // Patient Preferences
    preferences: {
      care_goal: "comfort",
      notes: ""
    },
    // Imminent Trigger
    imminent: {
      death_imminent: false,
      active_dying_assessment: false
    },
    // Voice Dictation Notes
    voice_notes: {
      physical: "",
      symptoms: "",
      functional: "",
      preferences: "",
      imminent: ""
    },
    // Metadata
    assessment_time: new Date().toISOString(),
    estimated_minutes: 0
  });

  // Voice Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  const steps = [
    { id: "physical", title: "Physical Snapshot", duration: "3 min", icon: Activity },
    { id: "symptoms", title: "Symptom Impact", duration: "2 min", icon: Thermometer },
    { id: "functional", title: "Functional Status", duration: "2 min", icon: Footprints },
    { id: "preferences", title: "Patient Preferences", duration: "2 min", icon: MessageSquare },
    { id: "imminent", title: "Imminent Trigger", duration: "1 min", icon: AlertTriangle },
  ];

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        await processRecording();
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      toast.info("Recording started - speak your assessment notes");
    } catch (error) {
      toast.error("Could not access microphone. Please check permissions.");
      console.error("Microphone access error:", error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const processRecording = async () => {
    if (audioChunksRef.current.length === 0) return;

    setIsTranscribing(true);
    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      const reader = new FileReader();
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        try {
          const response = await api.post('/voice-chat/transcribe', {
            audio_base64: base64Audio,
            context: 'clinical_note',
            language: 'en'
          });
          
          if (response.data.success && response.data.transcription) {
            const transcribedText = response.data.transcription;
            const currentStepId = steps[currentStep].id;
            
            // Append transcribed text to voice notes for current step
            setAssessmentData(prev => ({
              ...prev,
              voice_notes: {
                ...prev.voice_notes,
                [currentStepId]: prev.voice_notes[currentStepId] 
                  ? `${prev.voice_notes[currentStepId]} ${transcribedText}`
                  : transcribedText
              }
            }));
            
            // Process transcribed text for keyword mapping
            await processVoiceKeywords(transcribedText, currentStepId);
            
            toast.success("Transcription complete!");
          } else {
            toast.error("Transcription failed. Please try again.");
          }
        } catch (error) {
          console.error("Transcription error:", error);
          toast.error("Transcription failed. Please try again.");
        }
        setIsTranscribing(false);
      };
      
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error("Processing error:", error);
      setIsTranscribing(false);
      toast.error("Failed to process recording");
    }
  };

  // AI Keyword Mapping from voice input
  const processVoiceKeywords = async (text, stepId) => {
    const textLower = text.toLowerCase();
    
    if (stepId === 'symptoms') {
      // Map pain keywords
      if (textLower.includes('severe pain') || textLower.includes('pain is severe') || textLower.includes('10 out of 10')) {
        updateAssessment('symptoms', 'pain', 3);
        toast.info("Pain auto-set to Severe (3) based on voice input");
      } else if (textLower.includes('moderate pain') || textLower.includes('pain is moderate')) {
        updateAssessment('symptoms', 'pain', 2);
        toast.info("Pain auto-set to Moderate (2) based on voice input");
      } else if (textLower.includes('mild pain') || textLower.includes('pain is mild')) {
        updateAssessment('symptoms', 'pain', 1);
        toast.info("Pain auto-set to Mild (1) based on voice input");
      }
      
      // Map dyspnea keywords
      if (textLower.includes('severe shortness') || textLower.includes('dyspnea at rest') || textLower.includes('sob at rest')) {
        updateAssessment('symptoms', 'dyspnea', 3);
        toast.info("Dyspnea auto-set to Severe (3) based on voice input");
      } else if (textLower.includes('moderate shortness') || textLower.includes('dyspnea with exertion')) {
        updateAssessment('symptoms', 'dyspnea', 2);
        toast.info("Dyspnea auto-set to Moderate (2) based on voice input");
      }
      
      // Map anxiety keywords
      if (textLower.includes('anxious') || textLower.includes('agitated')) {
        updateAssessment('symptoms', 'anxiety', 2);
        toast.info("Anxiety flagged based on voice input");
      }
      
      // Map nausea keywords  
      if (textLower.includes('nausea') || textLower.includes('vomiting')) {
        updateAssessment('symptoms', 'nausea', 2);
        toast.info("Nausea flagged based on voice input");
      }
    }
    
    if (stepId === 'physical') {
      // Map skin keywords
      if (textLower.includes('pressure ulcer') || textLower.includes('pressure sore') || textLower.includes('bedsore')) {
        updateAssessment('skin', 'status', 'pressure');
        toast.info("Pressure ulcer detected - staging required");
      } else if (textLower.includes('skin intact') || textLower.includes('no wounds')) {
        updateAssessment('skin', 'status', 'intact');
      }
      
      // Map respiratory keywords
      if (textLower.includes('shortness of breath at rest') || textLower.includes('sob at rest')) {
        updateAssessment('respiratory', 'status', 'sob_rest');
        toast.warning("SOB at Rest flagged - SFV required within 48 hours");
      } else if (textLower.includes('shortness with exertion') || textLower.includes('sob with exertion')) {
        updateAssessment('respiratory', 'status', 'sob_exertion');
      }
    }
    
    if (stepId === 'preferences') {
      // Map care goal keywords
      if (textLower.includes('comfort') || textLower.includes('quality of life') || textLower.includes('symptom management')) {
        updateAssessment('preferences', 'care_goal', 'comfort');
        toast.info("Care goal set to Comfort Focused based on voice input");
      } else if (textLower.includes('spiritual') || textLower.includes('religious') || textLower.includes('faith')) {
        updateAssessment('preferences', 'care_goal', 'spiritual');
        toast.info("Care goal set to Spiritual/Religious based on voice input");
      }
    }
    
    if (stepId === 'imminent') {
      if (textLower.includes('actively dying') || textLower.includes('death imminent') || textLower.includes('terminal')) {
        updateAssessment('imminent', 'death_imminent', true);
        toast.warning("Imminent death flagged - Chaplain and Social Work notified");
      }
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const updateAssessment = useCallback((section, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  }, []);

  const handleSymptomChange = (symptomId, value) => {
    updateAssessment('symptoms', symptomId, value[0]);
    
    // Auto-flag if moderate or severe
    if ((symptomId === 'pain' || symptomId === 'dyspnea') && value[0] >= 2) {
      // Will trigger SFV requirement
      toast.warning(`${symptomId === 'pain' ? 'Pain' : 'Dyspnea'} marked as ${value[0] >= 3 ? 'severe' : 'moderate'}. SFV required within 48 hours.`);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      // Calculate estimated time based on completion
      const estimatedMinutes = steps.reduce((acc, step, idx) => {
        return acc + parseInt(step.duration);
      }, 0);
      
      const finalData = {
        ...assessmentData,
        estimated_minutes: estimatedMinutes,
        completed_at: new Date().toISOString()
      };

      if (onComplete) {
        await onComplete(finalData);
      }
      
      toast.success("HOPE Assessment completed successfully!");
    } catch (error) {
      toast.error("Failed to submit assessment");
    }
  };

  const getSeverityColor = (value) => {
    if (value === 0) return "bg-green-100 text-green-800";
    if (value === 1) return "bg-yellow-100 text-yellow-800";
    if (value === 2) return "bg-orange-100 text-orange-800";
    return "bg-red-100 text-red-800";
  };

  const getSeverityLabel = (value) => {
    const labels = ["None", "Mild", "Moderate", "Severe"];
    return labels[value] || "Unknown";
  };

  // Render step content
  const renderStepContent = () => {
    switch (steps[currentStep].id) {
      case "physical":
        return (
          <div className="space-y-6">
            {/* Body Map Interface */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Interactive Body Map */}
              <Card className="relative">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Body Map - Tap to Assess</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative w-full h-[400px] bg-gradient-to-b from-slate-50 to-slate-100 rounded-lg">
                    {/* Simple body outline SVG */}
                    <svg viewBox="0 0 100 100" className="w-full h-full opacity-20">
                      <ellipse cx="50" cy="12" rx="10" ry="12" fill="currentColor" />
                      <rect x="40" y="24" width="20" height="30" rx="3" fill="currentColor" />
                      <rect x="25" y="26" width="12" height="4" rx="2" fill="currentColor" />
                      <rect x="63" y="26" width="12" height="4" rx="2" fill="currentColor" />
                      <rect x="22" y="30" width="8" height="25" rx="2" fill="currentColor" />
                      <rect x="70" y="30" width="8" height="25" rx="2" fill="currentColor" />
                      <rect x="42" y="54" width="7" height="35" rx="2" fill="currentColor" />
                      <rect x="51" y="54" width="7" height="35" rx="2" fill="currentColor" />
                    </svg>
                    
                    {/* Clickable regions */}
                    {BODY_REGIONS.map(region => {
                      const Icon = region.icon;
                      return (
                        <button
                          key={region.id}
                          className="absolute transform -translate-x-1/2 -translate-y-1/2 p-2 rounded-full bg-white shadow-lg border-2 border-primary/20 hover:border-primary hover:scale-110 transition-all"
                          style={{ left: `${region.x}%`, top: `${region.y}%` }}
                          onClick={() => toast.info(`Assessing: ${region.label}`)}
                          data-testid={`body-region-${region.id}`}
                        >
                          <Icon className="w-5 h-5 text-primary" />
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Assessment Options */}
              <div className="space-y-4">
                {/* Skin Assessment */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Droplets className="w-4 h-4" />
                      Skin (Section M)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {["intact", "pressure", "non_pressure"].map(status => (
                        <Button
                          key={status}
                          variant={assessmentData.skin.status === status ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            updateAssessment('skin', 'status', status);
                            if (status === "pressure") {
                              toast.info("Staging required for HOPE Section M. Stage now?");
                            }
                          }}
                          data-testid={`skin-status-${status}`}
                        >
                          {status === "intact" ? "Intact" : status === "pressure" ? "Pressure Ulcer" : "Non-Pressure"}
                        </Button>
                      ))}
                    </div>
                    {assessmentData.skin.status === "pressure" && (
                      <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm font-medium text-amber-800">Staging Required</p>
                        <div className="flex gap-2 mt-2">
                          {[1, 2, 3, 4].map(stage => (
                            <Button
                              key={stage}
                              variant={assessmentData.skin.stage === stage ? "default" : "outline"}
                              size="sm"
                              onClick={() => updateAssessment('skin', 'stage', stage)}
                            >
                              Stage {stage}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Respiratory Assessment */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Wind className="w-4 h-4" />
                      Respiratory (Section O)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: "no_sob", label: "No SOB" },
                        { id: "sob_exertion", label: "SOB with Exertion" },
                        { id: "sob_rest", label: "SOB at Rest" }
                      ].map(option => (
                        <Button
                          key={option.id}
                          variant={assessmentData.respiratory.status === option.id ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            updateAssessment('respiratory', 'status', option.id);
                            if (option.id === "sob_rest") {
                              toast.warning("SOB at Rest flagged for Symptom Follow-up (SFV) requirement.");
                            }
                          }}
                          data-testid={`respiratory-${option.id}`}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                    {assessmentData.respiratory.status === "sob_rest" && (
                      <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                          <span className="text-sm font-medium text-red-800">SFV Required within 48 hours</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case "symptoms":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Symptom Impact Assessment (0-3 Scale)</CardTitle>
                <CardDescription>Use the sliders to rate each symptom. Moderate/Severe triggers follow-up.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {SYMPTOM_SCALES.map(symptom => (
                  <div key={symptom.id} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: symptom.color }}
                        />
                        <span className="font-medium">{symptom.label}</span>
                      </div>
                      <Badge className={getSeverityColor(assessmentData.symptoms[symptom.id])}>
                        {getSeverityLabel(assessmentData.symptoms[symptom.id])}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-muted-foreground w-8">0</span>
                      <Slider
                        value={[assessmentData.symptoms[symptom.id]]}
                        onValueChange={(value) => handleSymptomChange(symptom.id, value)}
                        max={3}
                        step={1}
                        className="flex-1"
                        data-testid={`symptom-slider-${symptom.id}`}
                      />
                      <span className="text-xs text-muted-foreground w-8">3</span>
                    </div>
                    
                    {/* Smart prompt for new/unmanaged symptoms */}
                    {assessmentData.symptoms[symptom.id] >= 2 && (symptom.id === 'pain' || symptom.id === 'dyspnea') && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm font-medium text-amber-800">
                          You marked {symptom.label} as {getSeverityLabel(assessmentData.symptoms[symptom.id])}. Is this NEW or UNMANAGED?
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            variant={assessmentData.symptomFlags[`${symptom.id}_new`] ? "default" : "outline"}
                            onClick={() => {
                              updateAssessment('symptomFlags', `${symptom.id}_new`, true);
                              toast.info("48-hour follow-up task created. Update MAR or order DME?");
                            }}
                          >
                            Yes - New/Unmanaged
                          </Button>
                          <Button
                            size="sm"
                            variant={!assessmentData.symptomFlags[`${symptom.id}_new`] ? "secondary" : "outline"}
                            onClick={() => updateAssessment('symptomFlags', `${symptom.id}_new`, false)}
                          >
                            No - Expected/Managed
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        );

      case "functional":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Functional Abilities Check (Section GG)</CardTitle>
                <CardDescription>Since the last visit, is the patient more dependent for:</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {ADL_ITEMS.map(adl => (
                    <div
                      key={adl.id}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        assessmentData.functional.changedADLs.includes(adl.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => {
                        const current = assessmentData.functional.changedADLs;
                        const updated = current.includes(adl.id)
                          ? current.filter(id => id !== adl.id)
                          : [...current, adl.id];
                        updateAssessment('functional', 'changedADLs', updated);
                        
                        if (!current.includes(adl.id)) {
                          toast.info(`HHA Plan of Care updated: Assist with ${adl.label}`);
                        }
                      }}
                      data-testid={`adl-${adl.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox checked={assessmentData.functional.changedADLs.includes(adl.id)} />
                        <span className="font-medium">{adl.label}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {assessmentData.functional.changedADLs.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-blue-800">Auto-Updated HHA Plan of Care</span>
                    </div>
                    <ul className="text-sm text-blue-700 space-y-1">
                      {assessmentData.functional.changedADLs.map(id => {
                        const adl = ADL_ITEMS.find(a => a.id === id);
                        return <li key={id}>• Assist with {adl?.label}</li>;
                      })}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case "preferences":
        return (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Patient/Family Care Goals</CardTitle>
                <CardDescription>HOPE requires documentation of the patient&apos;s care preferences</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {CARE_GOALS.map(goal => (
                    <div
                      key={goal.id}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        assessmentData.preferences.care_goal === goal.id
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => updateAssessment('preferences', 'care_goal', goal.id)}
                      data-testid={`goal-${goal.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          assessmentData.preferences.care_goal === goal.id
                            ? 'border-primary bg-primary'
                            : 'border-gray-300'
                        }`} />
                        <div>
                          <p className="font-medium">{goal.label}</p>
                          <p className="text-sm text-muted-foreground">{goal.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {assessmentData.preferences.care_goal && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800">
                        <span className="font-medium">Smart Text Generated:</span> The patient/caregiver expresses that 
                      [{assessmentData.preferences.care_goal === 'comfort' ? 'Comfort' : 
                        assessmentData.preferences.care_goal === 'longevity' ? 'Longevity/Treatment' : 'Spiritual'
                      }] is the primary goal at this time. IDG notified via dashboard.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case "imminent":
        return (
          <div className="space-y-6">
            <Card className={assessmentData.imminent.death_imminent ? "border-red-300 bg-red-50" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className={assessmentData.imminent.death_imminent ? "text-red-600" : ""} />
                  Imminent Status (Section J)
                </CardTitle>
                <CardDescription>A single toggle to indicate imminent death status</CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className={`p-6 rounded-lg border-2 cursor-pointer transition-all ${
                    assessmentData.imminent.death_imminent
                      ? 'border-red-500 bg-red-100'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                  onClick={() => {
                    const newValue = !assessmentData.imminent.death_imminent;
                    updateAssessment('imminent', 'death_imminent', newValue);
                    if (newValue) {
                      toast.warning("Active Dying assessment required. Chaplain and Social Work auto-notified for Imminent Support visits.");
                    }
                  }}
                  data-testid="imminent-toggle"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        assessmentData.imminent.death_imminent
                          ? 'border-red-500 bg-red-500'
                          : 'border-gray-300'
                      }`}>
                        {assessmentData.imminent.death_imminent && (
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-lg">Is Death Imminent?</p>
                        <p className="text-sm text-muted-foreground">
                          Toggle if patient is showing signs of active dying
                        </p>
                      </div>
                    </div>
                    <Badge variant={assessmentData.imminent.death_imminent ? "destructive" : "secondary"}>
                      {assessmentData.imminent.death_imminent ? "YES" : "NO"}
                    </Badge>
                  </div>
                </div>

                {assessmentData.imminent.death_imminent && (
                  <div className="mt-4 space-y-4">
                    <div className="p-4 bg-red-100 rounded-lg border border-red-300">
                      <h4 className="font-medium text-red-800 mb-2">Auto-Triggered Actions:</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        <li>• Active Dying Assessment form opened</li>
                        <li>• Chaplain notified for Imminent Support visit</li>
                        <li>• Social Worker notified for Imminent Support visit</li>
                        <li>• Family notification prompt generated</li>
                      </ul>
                    </div>
                    
                    <Button 
                      className="w-full"
                      variant="destructive"
                      onClick={() => updateAssessment('imminent', 'active_dying_assessment', true)}
                    >
                      Complete Active Dying Assessment
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">10-Minute HOPE Navigator</h2>
          <p className="text-muted-foreground">Quick RN Assessment Workflow</p>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-muted-foreground" />
          <span className="text-lg font-medium">
            ~{steps.reduce((acc, s) => acc + parseInt(s.duration), 0)} min total
          </span>
        </div>
      </div>

      {/* Step Progress */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const isActive = idx === currentStep;
          const isComplete = idx < currentStep;
          
          return (
            <React.Fragment key={step.id}>
              <button
                onClick={() => setCurrentStep(idx)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : isComplete
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`step-${step.id}`}
              >
                {isComplete ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
                <span className="font-medium">{step.title}</span>
                <Badge variant="secondary" className="text-xs">{step.duration}</Badge>
              </button>
              {idx < steps.length - 1 && (
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">
        {renderStepContent()}
      </div>

      {/* Voice Dictation Panel */}
      <Card className="border-2 border-dashed border-blue-200 bg-blue-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mic className="w-5 h-5 text-blue-600" />
            Voice Dictation
            <Badge variant="secondary" className="ml-2">AI-Powered</Badge>
          </CardTitle>
          <CardDescription>
            Speak your assessment notes - AI will transcribe and auto-map to fields
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={isRecording ? stopRecording : startRecording}
              variant={isRecording ? "destructive" : "default"}
              size="lg"
              disabled={isTranscribing}
              className="flex-shrink-0"
              data-testid="voice-record-btn"
            >
              {isTranscribing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Transcribing...
                </>
              ) : isRecording ? (
                <>
                  <MicOff className="w-5 h-5 mr-2" />
                  Stop ({formatDuration(recordingDuration)})
                </>
              ) : (
                <>
                  <Mic className="w-5 h-5 mr-2" />
                  Start Recording
                </>
              )}
            </Button>
            
            {isRecording && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-sm text-red-600 font-medium">Recording...</span>
              </div>
            )}
          </div>
          
          {/* Voice Notes for Current Step */}
          {assessmentData.voice_notes[steps[currentStep].id] && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Transcribed Notes for {steps[currentStep].title}:
              </label>
              <Textarea
                value={assessmentData.voice_notes[steps[currentStep].id]}
                onChange={(e) => setAssessmentData(prev => ({
                  ...prev,
                  voice_notes: {
                    ...prev.voice_notes,
                    [steps[currentStep].id]: e.target.value
                  }
                }))}
                placeholder="Transcribed voice notes will appear here..."
                className="min-h-[100px] bg-white"
                data-testid="voice-notes-textarea"
              />
            </div>
          )}
          
          <div className="text-xs text-muted-foreground">
            <Volume2 className="w-3 h-3 inline mr-1" />
            Tip: Say &quot;severe pain&quot; or &quot;SOB at rest&quot; to auto-set assessment fields
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={handlePrev}
          disabled={currentStep === 0}
          data-testid="prev-step"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        <div className="flex gap-2">
          {onSave && (
            <Button variant="outline" onClick={() => onSave(assessmentData)}>
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
          )}
          
          {currentStep < steps.length - 1 ? (
            <Button onClick={handleNext} data-testid="next-step">
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700" data-testid="submit-assessment">
              <Send className="w-4 h-4 mr-2" />
              Complete Assessment
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HOPENavigator;
